import React from 'react'

import DcFlexBox from '../dc-flex-box/dc-flex-box'
import DcText from '../dc-text/dc-text'
import { WarehouseItem } from '../dc-demand-forecast-card/dc-demand-forecast-card'

export interface BomCardMetric {
  label: string
  value: string | number
  icon: React.ComponentType<{ color?: string; size?: number | string }>
  iconColor: string
  badgeBg: string
  badgeBorder: string
  valueColor: string
}

export interface DcBomCardProps {
  componentName: string
  componentSku: string
  metrics: BomCardMetric[]
  warehouses?: WarehouseItem[]
  className?: string
  style?: React.CSSProperties
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
}

export const DcBomCard: React.FC<DcBomCardProps> = ({
  componentName,
  componentSku,
  metrics,
  warehouses,
  className,
  style,
  onMouseEnter,
  onMouseLeave,
}) => {
  return (
    <div
      className={className}
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '12px 16px',
        background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
        boxShadow:
          '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          '0 8px 16px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
        e.currentTarget.style.borderColor = '#cbd5e1'
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.background =
          'linear-gradient(to bottom, #ffffff, #f8f9fa)'
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow =
          '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
        e.currentTarget.style.borderColor = '#e5e7eb'
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.background =
          'linear-gradient(to bottom, #ffffff, #fafafa)'
        onMouseLeave?.(e)
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
        }}
      >
        {/* Modern Icon */}
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)',
            position: 'relative',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#ffffff',
              textAlign: 'center',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            {componentName.substring(0, 2).toUpperCase()}
          </div>
        </div>

        {/* Component Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <DcText
            style={{
              fontWeight: 700,
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: '2px',
              color: '#111827',
              letterSpacing: '-0.01em',
            }}
          >
            {componentName}
          </DcText>
          <DcText
            style={{
              fontSize: '11px',
              color: '#6b7280',
              fontWeight: 500,
            }}
          >
            SKU: {componentSku}
          </DcText>
        </div>

        {/* Modern Metrics */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {metrics.map((metric, index) => (
            <div
              key={index}
              style={{
                textAlign: 'right',
                padding: '6px 10px',
                borderRadius: '8px',
                background: metric.badgeBg,
                border: metric.badgeBorder,
                minWidth: '80px',
              }}
            >
              <DcFlexBox
                alignItems="center"
                justifyContent="flex-end"
                gap="4px"
                height="auto"
                style={{ marginBottom: '2px' }}
              >
                {React.createElement(metric.icon, {
                  color: metric.iconColor,
                  size: 11,
                })}
                <DcText
                  style={{
                    fontSize: '9px',
                    color: '#6b7280',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                  }}
                >
                  {metric.label}
                </DcText>
              </DcFlexBox>
              <DcText
                style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: metric.valueColor,
                }}
              >
                {metric.value}
              </DcText>
            </div>
          ))}
        </div>
      </div>

      {/* Warehouse Stock Breakdown */}
      {warehouses && warehouses.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            border: '1px solid #f3f4f6',
          }}
        >
          <DcFlexBox flexDirection="row" flexWrap="wrap" gap="10px" height="auto">
            {warehouses.map((warehouse, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ffffff',
                  borderRadius: '6px',
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 1px 1px rgba(0, 0, 0, 0.02)',
                  minWidth: 'fit-content',
                }}
              >
                <DcText
                  style={{
                    fontSize: '10px',
                    color: '#64748b',
                    fontWeight: 500,
                  }}
                >
                  {warehouse.warehouse_name}
                </DcText>
                <div
                  style={{
                    height: '10px',
                    width: '1px',
                    backgroundColor: '#e2e8f0',
                  }}
                />
                <DcText
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: '#0f172a',
                  }}
                >
                  {warehouse.stock.toLocaleString()}
                </DcText>
              </div>
            ))}
          </DcFlexBox>
        </div>
      )}
    </div>
  )
}

export default DcBomCard

